/**
 * React Query Configuration Constants
 *
 * Centralized configuration for TanStack React Query including:
 * - Cache times and stale times
 * - Retry logic
 * - Refetch behavior
 * - Network mode settings
 */

// ============================================================================
// CACHE & STALE TIME CONFIGURATION
// ============================================================================

/**
 * Default cache time (5 minutes)
 * How long unused data remains in cache before garbage collection
 */
export const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Default stale time (1 minute)
 * How long data is considered fresh before refetching
 */
export const DEFAULT_STALE_TIME = 1 * 60 * 1000; // 1 minute

/**
 * Cache times for different data types
 */
export const CACHE_TIME = {
  /** Static data that rarely changes (30 minutes) */
  STATIC: 30 * 60 * 1000,
  /** User data (10 minutes) */
  USER: 10 * 60 * 1000,
  /** List data (5 minutes) */
  LIST: 5 * 60 * 1000,
  /** Detail data (5 minutes) */
  DETAIL: 5 * 60 * 1000,
  /** Dashboard/stats (2 minutes) */
  DASHBOARD: 2 * 60 * 1000,
  /** Real-time data (30 seconds) */
  REALTIME: 30 * 1000,
} as const;

/**
 * Stale times for different data types
 */
export const STALE_TIME = {
  /** Static data (10 minutes) */
  STATIC: 10 * 60 * 1000,
  /** User data (5 minutes) */
  USER: 5 * 60 * 1000,
  /** List data (1 minute) */
  LIST: 1 * 60 * 1000,
  /** Detail data (2 minutes) */
  DETAIL: 2 * 60 * 1000,
  /** Dashboard/stats (30 seconds) */
  DASHBOARD: 30 * 1000,
  /** Real-time data (0 - always stale) */
  REALTIME: 0,
  /** Infinite (never stale) */
  INFINITE: Infinity,
} as const;

// ============================================================================
// RETRY CONFIGURATION
// ============================================================================

/**
 * Default retry count for failed queries
 */
export const DEFAULT_RETRY_COUNT = 3;

/**
 * Default retry delay (exponential backoff)
 * @param attemptIndex - Current attempt number (0-indexed)
 */
export const DEFAULT_RETRY_DELAY = (attemptIndex: number): number => {
  return Math.min(1000 * 2 ** attemptIndex, 30000); // Max 30 seconds
};

/**
 * Retry configuration by operation type
 */
export const RETRY_CONFIG = {
  /** Queries - retry 3 times */
  QUERY: 3,
  /** Mutations - don't retry by default */
  MUTATION: 0,
  /** Critical queries - retry 5 times */
  CRITICAL: 5,
  /** Background queries - retry once */
  BACKGROUND: 1,
} as const;

// ============================================================================
// REFETCH CONFIGURATION
// ============================================================================

/**
 * Refetch on window focus
 */
export const REFETCH_ON_WINDOW_FOCUS = true;

/**
 * Refetch on reconnect
 */
export const REFETCH_ON_RECONNECT = true;

/**
 * Refetch on mount
 */
export const REFETCH_ON_MOUNT = true;

/**
 * Refetch interval (disabled by default)
 */
export const REFETCH_INTERVAL = false;

// ============================================================================
// NETWORK MODE
// ============================================================================

/**
 * Network mode for queries
 * - 'online': Only run when online
 * - 'always': Run regardless of network status
 * - 'offlineFirst': Try offline cache first
 */
export const NETWORK_MODE = 'online' as const;

// ============================================================================
// QUERY KEY PREFIXES
// ============================================================================

/**
 * Query key prefixes for different entity types
 * Used by query key factories
 */
export const QUERY_KEY_PREFIX = {
  /** Authentication queries */
  AUTH: 'auth',
  /** User queries */
  USERS: 'users',
  /** Policy queries */
  POLICIES: 'policies',
  /** Claim queries */
  CLAIMS: 'claims',
  /** Customer queries */
  CUSTOMERS: 'customers',
  /** Product queries */
  PRODUCTS: 'products',
  /** Report queries */
  REPORTS: 'reports',
  /** Dashboard queries */
  DASHBOARD: 'dashboard',
  /** Notification queries */
  NOTIFICATIONS: 'notifications',
  /** Settings queries */
  SETTINGS: 'settings',
  /** File queries */
  FILES: 'files',
  /** Audit log queries */
  AUDIT: 'audit',
  /** Search queries */
  SEARCH: 'search',
} as const;

// ============================================================================
// MUTATION CONFIGURATION
// ============================================================================

/**
 * Default mutation configuration
 */
export const MUTATION_CONFIG = {
  /** Show loading toast on mutation start */
  SHOW_LOADING_TOAST: false,
  /** Show success toast on mutation success */
  SHOW_SUCCESS_TOAST: true,
  /** Show error toast on mutation error */
  SHOW_ERROR_TOAST: true,
  /** Invalidate related queries on success */
  INVALIDATE_ON_SUCCESS: true,
  /** Refetch active queries on success */
  REFETCH_ON_SUCCESS: true,
} as const;

// ============================================================================
// PAGINATION CONFIGURATION
// ============================================================================

/**
 * Default pagination settings
 */
export const PAGINATION_CONFIG = {
  /** Default page size */
  DEFAULT_PAGE_SIZE: 10,
  /** Available page size options */
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  /** Default page number (0-indexed) */
  DEFAULT_PAGE: 0,
  /** Keep previous data while fetching new page */
  KEEP_PREVIOUS_DATA: true,
} as const;

// ============================================================================
// INFINITE QUERY CONFIGURATION
// ============================================================================

/**
 * Infinite query configuration
 */
export const INFINITE_QUERY_CONFIG = {
  /** Default page size for infinite queries */
  DEFAULT_PAGE_SIZE: 20,
  /** Max pages to keep in cache */
  MAX_PAGES: 5,
  /** Get next page param function */
  GET_NEXT_PAGE_PARAM: (lastPage: any, allPages: any[]) => {
    return lastPage.hasMore ? allPages.length : undefined;
  },
} as const;

// ============================================================================
// DEVTOOLS CONFIGURATION
// ============================================================================

/**
 * React Query DevTools configuration
 */
export const DEVTOOLS_CONFIG = {
  /** Initial open state */
  INITIAL_IS_OPEN: false,
  /** Position on screen */
  POSITION: 'bottom-right' as const,
  /** Enable in production */
  ENABLE_IN_PRODUCTION: false,
} as const;

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type QueryKeyPrefix = (typeof QUERY_KEY_PREFIX)[keyof typeof QUERY_KEY_PREFIX];
export type NetworkMode = typeof NETWORK_MODE;
